import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Truck } from 'lucide-react';
import { DeliveryWindowBadge } from '../../../../components/delivery/DeliveryDateBadges';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../components/ui/PaginatedListLoading';
import { formatOrderReference } from '../../../../utils/formatOrderReference';
import CarrierBatchShipPanel from '../../orders/components/CarrierBatchShipPanel';
import { USE_CARRIER_FULFILLMENT } from '../../fulfillmentConfig';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

/** Separator for grouping orders by address (avoids ambiguity if city/country contain "|"). */
const AREA_GROUP_SEP = '\u0001';

type ShippingAgentOption = {
  id: string;
  email: string;
  fullName: string | null;
};

type BatchListRow = {
  id: string;
  name: string | null;
  areaLabel: string;
  deliverySpeed: 'STANDARD' | 'EXPRESS';
  country: string | null;
  cityKey: string | null;
  windowStart: string;
  windowEnd: string;
  status: string;
  orderCount: number;
  dispatchJobStatus?: string;
  dispatchJobTotal?: number | null;
  dispatchJobProcessed?: number;
  assignedShippingAgentId?: string | null;
  assignedShippingAgent?: ShippingAgentOption | null;
  createdAt: string;
};

type BatchDetail = {
  id: string;
  name: string | null;
  areaLabel: string;
  deliverySpeed: 'STANDARD' | 'EXPRESS';
  country: string | null;
  cityKey: string | null;
  windowStart: string;
  windowEnd: string;
  status: string;
  notes: string | null;
  assignedShippingAgentId?: string | null;
  assignedShippingAgent?: ShippingAgentOption | null;
  dispatchJobStatus?: string;
  dispatchJobTotal?: number | null;
  dispatchJobProcessed?: number;
  dispatchJobFailed?: number;
  dispatchJobErrors?: Array<{ orderId: string; message: string }> | null;
  dispatchJobStartedAt?: string | null;
  dispatchJobCompletedAt?: string | null;
  dispatchStats?: {
    attempted: number;
    shipped: number;
    left: number;
    failed: number;
    reasonBreakdown: Array<{ reason: string; count: number }>;
  };
  orders: Array<{
    id: string;
    totalUsd: number | string;
    status: string;
    deliverySpeed: string;
    createdAt: string;
    addressSnapshot: { city?: string; country?: string };
    customer?: {
      email: string;
      customerProfile?: { fullName?: string | null; phone?: string | null } | null;
    };
    items: Array<{ productNameSnapshot: string; qty: number; unit: string }>;
    carrierServiceCode?: string | null;
    carrierServiceName?: string | null;
    addressVerification?: { status?: string } | null;
    deliveryBatch?: { id: string; areaLabel: string; name: string | null } | null;
  }>;
};

function presentBatchStatus(status: string): string {
  return status === 'CLOSED' ? 'ARCHIVED' : status;
}

export default function WorkspaceDeliveryBatchesPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { authorizedFetch, auth } = useAuth();
  const globalAuthorizedFetch = useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      // Delivery batches workspace is intentionally global (not department-scoped).
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const permissions = auth.permissions || [];
  const canUpdate = permissions.includes('DELIVERY_BATCH:UPDATE');
  const canCreate = permissions.includes('DELIVERY_BATCH:CREATE');
  const canDelete = permissions.includes('DELIVERY_BATCH:DELETE');

  const [rows, setRows] = useState<BatchListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BatchDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [areaLabel, setAreaLabel] = useState('');
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [deliverySpeed, setDeliverySpeed] = useState<'STANDARD' | 'EXPRESS'>('STANDARD');
  const [saving, setSaving] = useState(false);
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [batchSearchText, setBatchSearchText] = useState('');
  const [batchSearchStatus, setBatchSearchStatus] = useState<
    'ACTIVE' | 'ALL' | 'OPEN' | 'SHIPPED' | 'DISPATCHED' | 'CLOSED'
  >('ACTIVE');
  const [batchSearchSpeed, setBatchSearchSpeed] = useState<'ALL' | 'STANDARD' | 'EXPRESS'>('ALL');
  const [batchSearchCountry, setBatchSearchCountry] = useState('');
  const [batchSearchCity, setBatchSearchCity] = useState('');

  const [searchResults, setSearchResults] = useState<BatchDetail['orders']>([]);
  const [assignSearchCountry, setAssignSearchCountry] = useState('');
  const [assignSearchTown, setAssignSearchTown] = useState('');
  const [assignSearchQuery, setAssignSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [countrySuggestions, setCountrySuggestions] = useState<string[]>([]);
  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const eligibleReqIdRef = useRef(0);
  const [lastSearchParams, setLastSearchParams] = useState<{
    country: string;
    town: string;
    q: string;
  } | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [assignBusy, setAssignBusy] = useState(false);
  const [dispatchNotice, setDispatchNotice] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editAreaLabel, setEditAreaLabel] = useState('');
  const [editName, setEditName] = useState('');
  const [editCountry, setEditCountry] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editWindowStart, setEditWindowStart] = useState('');
  const [editWindowEnd, setEditWindowEnd] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editDeliverySpeed, setEditDeliverySpeed] = useState<'STANDARD' | 'EXPRESS'>('STANDARD');
  const [editAssignedAgentId, setEditAssignedAgentId] = useState<string>('');
  const [shippingAgents, setShippingAgents] = useState<ShippingAgentOption[]>([]);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    kind: 'SHIP_NOW' | 'CONFIRM_DELIVERY' | 'CLOSE_BATCH' | 'DELETE' | null;
    batchId: string | null;
    note?: string;
    hasIssueOrders?: boolean;
    issueOrderIds?: string[];
    issueSearch?: string;
  }>({ open: false, kind: null, batchId: null });

  const toLocalDatetime = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/sales/delivery-batches`);
      if (batchSearchText.trim()) url.searchParams.set('q', batchSearchText.trim());
      if (
        batchSearchStatus !== 'ALL' &&
        batchSearchStatus !== 'ACTIVE'
      ) {
        url.searchParams.set('status', batchSearchStatus);
      }
      if (batchSearchSpeed !== 'ALL') url.searchParams.set('deliverySpeed', batchSearchSpeed);
      if (batchSearchCountry.trim()) url.searchParams.set('country', batchSearchCountry.trim());
      if (batchSearchCity.trim()) url.searchParams.set('city', batchSearchCity.trim());
      const res = await globalAuthorizedFetch(url.toString());
      if (!res.ok) throw new Error('load');
      const list = (await res.json()) as BatchListRow[];
      setRows(
        batchSearchStatus === 'ACTIVE'
          ? list.filter((r) => r.status !== 'CLOSED')
          : list,
      );
    } catch {
      setError(t('backoffice.delivery_batches_load_error'));
    } finally {
      setLoading(false);
    }
  }, [
    globalAuthorizedFetch,
    t,
    batchSearchText,
    batchSearchStatus,
    batchSearchSpeed,
    batchSearchCountry,
    batchSearchCity,
  ]);

  const loadDetail = useCallback(
    async (id: string) => {
      setDetailLoading(true);
      try {
        const res = await globalAuthorizedFetch(`${API_BASE}/sales/delivery-batches/${id}`);
        if (!res.ok) throw new Error('detail');
        setDetail((await res.json()) as BatchDetail);
      } catch {
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [globalAuthorizedFetch],
  );

  const fetchEligibleWithParams = useCallback(
    async (params: { country: string; town: string; q: string }, init?: RequestInit) => {
      const url = new URL(`${API_BASE}/sales/delivery-batches/eligible-orders`);
      if (params.country.trim()) url.searchParams.set('country', params.country.trim());
      if (params.town.trim()) url.searchParams.set('city', params.town.trim());
      if (params.q.trim()) url.searchParams.set('q', params.q.trim());
      if (detail?.deliverySpeed) url.searchParams.set('deliverySpeed', detail.deliverySpeed);
      const res = await globalAuthorizedFetch(url.toString(), init);
      if (!res.ok) throw new Error('eligible');
      return (await res.json()) as BatchDetail['orders'];
    },
    [detail?.deliverySpeed, globalAuthorizedFetch],
  );

  /** Real-time order search (debounced). */
  useEffect(() => {
    if (!detail) return;
    const params = {
      country: assignSearchCountry.trim(),
      town: assignSearchTown.trim(),
      q: assignSearchQuery.trim(),
    };
    if (!params.country && !params.town && !params.q) {
      setSearchResults([]);
      setLastSearchParams(null);
      setSearchLoading(false);
      return;
    }
    const myId = ++eligibleReqIdRef.current;
    const t = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const list = await fetchEligibleWithParams(params);
        if (eligibleReqIdRef.current === myId) {
          setSearchResults(list);
          setLastSearchParams(params);
        }
      } catch {
        if (eligibleReqIdRef.current === myId) {
          setSearchResults([]);
        }
      } finally {
        if (eligibleReqIdRef.current === myId) setSearchLoading(false);
      }
    }, 350);
    return () => window.clearTimeout(t);
  }, [
    assignSearchCountry,
    assignSearchTown,
    assignSearchQuery,
    detail,
    fetchEligibleWithParams,
  ]);

  /** Country autocomplete from PAID orders (debounced). */
  useEffect(() => {
    if (!detail) return;
    const t = window.setTimeout(async () => {
      try {
        const url = new URL(`${API_BASE}/sales/delivery-batches/address-suggestions`);
        url.searchParams.set('field', 'country');
        url.searchParams.set('q', assignSearchCountry);
        const res = await globalAuthorizedFetch(url.toString());
        if (!res.ok) return;
        const list = (await res.json()) as string[];
        setCountrySuggestions(Array.isArray(list) ? list : []);
      } catch {
        setCountrySuggestions([]);
      }
    }, 200);
    return () => window.clearTimeout(t);
  }, [assignSearchCountry, detail, globalAuthorizedFetch]);

  /** City autocomplete from PAID orders (debounced); narrows by country when set. */
  useEffect(() => {
    if (!detail) return;
    const t = window.setTimeout(async () => {
      try {
        const url = new URL(`${API_BASE}/sales/delivery-batches/address-suggestions`);
        url.searchParams.set('field', 'city');
        url.searchParams.set('q', assignSearchTown);
        if (assignSearchCountry.trim()) {
          url.searchParams.set('country', assignSearchCountry.trim());
        }
        const res = await globalAuthorizedFetch(url.toString());
        if (!res.ok) return;
        const list = (await res.json()) as string[];
        setCitySuggestions(Array.isArray(list) ? list : []);
      } catch {
        setCitySuggestions([]);
      }
    }, 200);
    return () => window.clearTimeout(t);
  }, [assignSearchTown, assignSearchCountry, detail, globalAuthorizedFetch]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      void loadList();
    }, 250);
    return () => window.clearTimeout(id);
  }, [loadList]);

  const listHasActiveDispatch = useMemo(
    () =>
      rows.some(
        (r) =>
          r.status === 'SHIPPED' &&
          (r.dispatchJobStatus === 'QUEUED' || r.dispatchJobStatus === 'RUNNING'),
      ),
    [rows],
  );

  useEffect(() => {
    if (!listHasActiveDispatch) return;
    const id = window.setInterval(() => {
      void loadList();
    }, 2500);
    return () => window.clearInterval(id);
  }, [listHasActiveDispatch, loadList]);

  useEffect(() => {
    const st = location.state as { focusBatchId?: string } | undefined;
    if (st?.focusBatchId) {
      setSelectedId(st.focusBatchId);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    if (!detail) return;
    setAssignSearchCountry(detail.country ?? '');
    setAssignSearchTown(detail.cityKey ?? '');
    setAssignSearchQuery('');
    setSearchResults([]);
    setLastSearchParams(null);
    setCountrySuggestions([]);
    setCitySuggestions([]);
  }, [detail]);

  useEffect(() => {
    if (!detail) return;
    setEditAreaLabel(detail.areaLabel ?? '');
    setEditName(detail.name ?? '');
    setEditCountry(detail.country ?? '');
    setEditCity(detail.cityKey ?? '');
    setEditWindowStart(toLocalDatetime(detail.windowStart));
    setEditWindowEnd(toLocalDatetime(detail.windowEnd));
    setEditNotes(detail.notes ?? '');
    setEditDeliverySpeed(detail.deliverySpeed ?? 'STANDARD');
    setEditAssignedAgentId(detail.assignedShippingAgentId ?? '');
    setEditOpen(false);
  }, [detail]);

  useEffect(() => {
    if (!canUpdate) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await globalAuthorizedFetch(
          `${API_BASE}/sales/delivery-batches/shipping-agents`,
        );
        if (!res.ok) return;
        const list = (await res.json()) as ShippingAgentOption[];
        if (!cancelled) setShippingAgents(list);
      } catch {
        // best-effort load; the dropdown stays empty if it fails.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUpdate, globalAuthorizedFetch]);

  const dispatchJobLive =
    detail?.status === 'SHIPPED' &&
    (detail.dispatchJobStatus === 'QUEUED' || detail.dispatchJobStatus === 'RUNNING');

  useEffect(() => {
    if (!selectedId || !dispatchJobLive) return;
    const id = window.setInterval(() => {
      void loadDetail(selectedId);
      void loadList();
    }, 2500);
    return () => window.clearInterval(id);
  }, [selectedId, dispatchJobLive, loadDetail, loadList]);

  useEffect(() => {
    setSelectedOrderIds([]);
  }, [selectedId]);

  const resetCreateForm = () => {
    setAreaLabel('');
    setName('');
    setCountry('');
    setCity('');
    setNotes('');
    setDeliverySpeed('STANDARD');
    const now = new Date();
    const end = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    const toLocal = (d: Date) => {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setWindowStart(toLocal(now));
    setWindowEnd(toLocal(end));
  };

  useEffect(() => {
    resetCreateForm();
  }, []);

  const handleCreate = async () => {
    if (!areaLabel.trim() || !windowStart || !windowEnd) return;
    setSaving(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/delivery-batches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          areaLabel: areaLabel.trim(),
          name: name.trim() || undefined,
          country: country.trim() || undefined,
          city: city.trim() || undefined,
          windowStart: new Date(windowStart).toISOString(),
          windowEnd: new Date(windowEnd).toISOString(),
          notes: notes.trim() || undefined,
          deliverySpeed,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'save');
      }
      const created = (await res.json()) as { id: string };
      resetCreateForm();
      await loadList();
      setSelectedId(created.id);
      setCreateFormOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('backoffice.delivery_batches_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const shipBatchNow = async (id: string) => {
    setSaving(true);
    setDispatchNotice(null);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(
        `${API_BASE}/sales/delivery-batches/${id}/ship-now`,
        {
          method: 'POST',
        },
      );
      const data = (await res.json().catch(() => ({}))) as {
        message?: string;
        queued?: boolean;
      };
      if (!res.ok) {
        const raw = data as { message?: string | string[] };
        const msg = Array.isArray(raw.message)
          ? raw.message.join(', ')
          : raw.message;
        throw new Error(msg || 'close');
      }
      setDispatchNotice(data.message ?? null);
      await loadList();
      if (selectedId === id) void loadDetail(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'close');
    } finally {
      setSaving(false);
    }
  };

  const removeBatch = async (id: string) => {
    setSaving(true);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/delivery-batches/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('delete');
      setSelectedId(null);
      await loadList();
    } finally {
      setSaving(false);
    }
  };

  const confirmBatchDelivery = async (
    id: string,
    input: {
      note?: string;
      issueOrderIds?: string[];
    },
  ) => {
    setSaving(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(
        `${API_BASE}/sales/delivery-batches/${id}/confirm-delivery`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'confirm');
      }
      await loadList();
      if (selectedId === id) void loadDetail(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'confirm');
    } finally {
      setSaving(false);
    }
  };

  const closeBatch = async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/delivery-batches/${id}/close`, {
        method: 'POST',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'close');
      }
      await loadList();
      if (selectedId === id) void loadDetail(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'close');
    } finally {
      setSaving(false);
    }
  };

  const openConfirmModal = (
    kind: 'SHIP_NOW' | 'CONFIRM_DELIVERY' | 'CLOSE_BATCH' | 'DELETE',
    batchId: string,
  ) => {
    setConfirmModal({
      open: true,
      kind,
      batchId,
      note: '',
      hasIssueOrders: false,
      issueOrderIds: [],
      issueSearch: '',
    });
  };

  const closeConfirmModal = () => {
    if (saving) return;
    setConfirmModal({ open: false, kind: null, batchId: null });
  };

  const runConfirmedAction = async () => {
    if (!confirmModal.batchId || !confirmModal.kind) return;
    const { batchId, kind } = confirmModal;
    if (kind === 'SHIP_NOW') {
      setConfirmModal({ open: false, kind: null, batchId: null });
      await shipBatchNow(batchId);
      return;
    }
    if (kind === 'CONFIRM_DELIVERY') {
      const payload: {
        note?: string;
        issueOrderIds?: string[];
      } = {
        note: confirmModal.note || undefined,
        issueOrderIds:
          confirmModal.hasIssueOrders &&
          Array.isArray(confirmModal.issueOrderIds) &&
          confirmModal.issueOrderIds.length > 0
            ? confirmModal.issueOrderIds
            : undefined,
      };
      setConfirmModal({ open: false, kind: null, batchId: null });
      await confirmBatchDelivery(batchId, payload);
      return;
    }
    setConfirmModal({ open: false, kind: null, batchId: null });
    if (kind === 'CLOSE_BATCH') {
      await closeBatch(batchId);
      return;
    }
    await removeBatch(batchId);
  };

  const handleUpdateBatch = async () => {
    if (!detail) return;
    if (!editAreaLabel.trim() || !editWindowStart || !editWindowEnd) return;
    setSaving(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/delivery-batches/${detail.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          areaLabel: editAreaLabel.trim(),
          name: editName.trim() || null,
          country: editCountry.trim() || null,
          city: editCity.trim() || null,
          windowStart: new Date(editWindowStart).toISOString(),
          windowEnd: new Date(editWindowEnd).toISOString(),
          notes: editNotes.trim() || null,
          deliverySpeed: editDeliverySpeed,
          assignedShippingAgentId: editAssignedAgentId
            ? editAssignedAgentId
            : null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const raw = (body as { message?: string | string[] }).message;
        const msg = Array.isArray(raw) ? raw.join(', ') : raw;
        throw new Error(msg || 'update');
      }
      setEditOpen(false);
      await loadList();
      await loadDetail(detail.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'update');
    } finally {
      setSaving(false);
    }
  };

  const assignSelectedOrders = async () => {
    if (!selectedId || selectedOrderIds.length === 0) return;
    setAssignBusy(true);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/delivery-batches/assign-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchId: selectedId, orderIds: selectedOrderIds }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'assign');
      }
      const payload = (await res.json()) as {
        okCount: number;
        total: number;
        results: Array<{ orderId: string; ok: boolean; error?: string }>;
      };
      const failed = payload.results.filter((r) => !r.ok);
      if (failed.length > 0) {
        setError(
          failed.map((f) => `${f.orderId.slice(0, 8)}: ${f.error ?? 'failed'}`).join(' · '),
        );
      } else {
        setError(null);
      }
      setSelectedOrderIds([]);
      await loadDetail(selectedId);
      await loadList();
      if (lastSearchParams) {
        try {
          const list = await fetchEligibleWithParams(lastSearchParams);
          setSearchResults(list);
        } catch {
          /* keep previous results */
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'assign');
    } finally {
      setAssignBusy(false);
    }
  };

  const toggleOrderSelected = (orderId: string) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId],
    );
  };

  const unassignOrder = async (orderId: string) => {
    setAssignBusy(true);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/delivery-batches/unassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId }),
      });
      if (!res.ok) throw new Error('unassign');
      if (selectedId) {
        await loadDetail(selectedId);
        await loadList();
        if (lastSearchParams) {
          try {
            const list = await fetchEligibleWithParams(lastSearchParams);
            setSearchResults(list);
          } catch {
            /* ignore */
          }
        }
      }
    } finally {
      setAssignBusy(false);
    }
  };

  const hasSearchInput = useMemo(
    () =>
      Boolean(
        assignSearchCountry.trim() ||
          assignSearchTown.trim() ||
          assignSearchQuery.trim(),
      ),
    [assignSearchCountry, assignSearchTown, assignSearchQuery],
  );

  const assignable = useMemo(
    () =>
      searchResults.filter((o) => !detail?.orders.some((x) => x.id === o.id)),
    [searchResults, detail],
  );

  const groupedByArea = useMemo(() => {
    const map = new Map<string, BatchDetail['orders']>();
    for (const o of assignable) {
      const snap = o.addressSnapshot as { city?: string; country?: string };
      const key = `${snap?.country ?? '—'}${AREA_GROUP_SEP}${snap?.city ?? '—'}`;
      const prev = map.get(key) ?? [];
      prev.push(o);
      map.set(key, prev);
    }
    return Array.from(map.entries());
  }, [assignable]);

  const selectAllShown = () => {
    setSelectedOrderIds(assignable.map((o) => o.id));
  };

  const selectAllInArea = (orderIds: string[]) => {
    setSelectedOrderIds((prev) => Array.from(new Set([...prev, ...orderIds])));
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={`${location.pathname}${location.search}`}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.delivery_batches_title')}
          </h1>
          <p className="max-w-3xl text-sm text-slate-600">
            {t('backoffice.delivery_batches_subtitle')}
          </p>
        </section>

      <div className="space-y-4">
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        ) : null}
        {dispatchNotice ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            {dispatchNotice}
          </div>
        ) : null}

        {canCreate ? (
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <button
              type="button"
              onClick={() => setCreateFormOpen((v) => !v)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <span className="text-sm font-semibold text-slate-900">
                {t('backoffice.delivery_batches_create')}
              </span>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-slate-500 transition-transform ${createFormOpen ? 'rotate-180' : ''}`}
                aria-hidden
              />
              <span className="sr-only">
                {createFormOpen
                  ? t('backoffice.delivery_batches_create_collapse')
                  : t('backoffice.delivery_batches_create_expand')}
              </span>
            </button>
            {createFormOpen ? (
              <div className="border-t border-slate-100 px-4 pb-4 pt-1">
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <label className="block text-xs font-medium text-slate-600">
                    {t('backoffice.delivery_batches_area_label')}
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                      value={areaLabel}
                      onChange={(e) => setAreaLabel(e.target.value)}
                      placeholder={t('backoffice.delivery_batches_area_ph')}
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-600">
                    {t('backoffice.delivery_batches_name_optional')}
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-600">
                    {t('backoffice.delivery_batches_speed', { defaultValue: 'Delivery speed' })}
                    <select
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                      value={deliverySpeed}
                      onChange={(e) => setDeliverySpeed((e.target.value as 'STANDARD' | 'EXPRESS') || 'STANDARD')}
                    >
                      <option value="STANDARD">STANDARD</option>
                      <option value="EXPRESS">EXPRESS</option>
                    </select>
                  </label>
                  <label className="block text-xs font-medium text-slate-600">
                    {t('backoffice.delivery_batches_country_optional')}
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-600">
                    {t('backoffice.delivery_batches_city_optional')}
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-600">
                    {t('backoffice.delivery_batches_window_start')}
                    <input
                      type="datetime-local"
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                      value={windowStart}
                      onChange={(e) => setWindowStart(e.target.value)}
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-600">
                    {t('backoffice.delivery_batches_window_end')}
                    <input
                      type="datetime-local"
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                      value={windowEnd}
                      onChange={(e) => setWindowEnd(e.target.value)}
                    />
                  </label>
                </div>
                <label className="mt-3 block text-xs font-medium text-slate-600">
                  {t('backoffice.delivery_batches_notes_optional')}
                  <textarea
                    className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  disabled={saving || !areaLabel.trim()}
                  onClick={() => void handleCreate()}
                  className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {saving ? t('common.loading') : t('backoffice.delivery_batches_save')}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[minmax(200px,260px)_minmax(0,1fr)] lg:items-start">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm lg:sticky lg:top-4">
            <div className="border-b border-slate-100 px-3 py-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t('backoffice.delivery_batches_list')}
              </h2>
              <div className="mt-2 grid gap-2">
                <input
                  type="search"
                  value={batchSearchText}
                  onChange={(e) => setBatchSearchText(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
                  placeholder={t('backoffice.delivery_batches_search_batches', { defaultValue: 'Search batches by name, area, notes...' })}
                />
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={batchSearchStatus}
                    onChange={(e) =>
                      setBatchSearchStatus(
                        (e.target.value as
                          | 'ACTIVE'
                          | 'ALL'
                          | 'OPEN'
                          | 'SHIPPED'
                          | 'DISPATCHED'
                          | 'CLOSED') || 'ACTIVE',
                      )
                    }
                    className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
                  >
                    <option value="ACTIVE">Processing (default)</option>
                    <option value="ALL">All status</option>
                    <option value="OPEN">OPEN</option>
                    <option value="SHIPPED">SHIPPED</option>
                    <option value="DISPATCHED">DISPATCHED</option>
                    <option value="CLOSED">ARCHIVED</option>
                  </select>
                  <select
                    value={batchSearchSpeed}
                    onChange={(e) => setBatchSearchSpeed((e.target.value as 'ALL' | 'STANDARD' | 'EXPRESS') || 'ALL')}
                    className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
                  >
                    <option value="ALL">All speed</option>
                    <option value="STANDARD">STANDARD</option>
                    <option value="EXPRESS">EXPRESS</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={batchSearchCountry}
                    onChange={(e) => setBatchSearchCountry(e.target.value)}
                    className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
                    placeholder="Country"
                  />
                  <input
                    value={batchSearchCity}
                    onChange={(e) => setBatchSearchCity(e.target.value)}
                    className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
                    placeholder="City"
                  />
                </div>
              </div>
            </div>
            {loading ? (
              <div className="p-3">
                <PaginatedListLoading
                  compact
                  minHeightClass="min-h-[180px]"
                  title={t('common.list_loading_title')}
                />
              </div>
            ) : rows.length === 0 ? (
              <p className="p-3 text-sm text-slate-600">{t('backoffice.delivery_batches_empty')}</p>
            ) : (
              <ul className="max-h-[min(65vh,calc(100vh-12rem))] divide-y divide-slate-100 overflow-y-auto overscroll-contain">
                {rows.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(r.id)}
                      className={[
                        'flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-slate-50',
                        selectedId === r.id ? 'bg-brand-50/50' : '',
                      ].join(' ')}
                    >
                      <Truck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-semibold text-slate-900">
                          {r.name || r.areaLabel}
                        </div>
                        {r.name ? (
                          <div className="truncate text-[11px] text-slate-500">{r.areaLabel}</div>
                        ) : null}
                        <div className="mt-1">
                          <DeliveryWindowBadge
                            windowStart={r.windowStart}
                            windowEnd={r.windowEnd}
                            fromLabel={t('account.delivery_window_from')}
                            untilLabel={t('account.delivery_window_until')}
                            variant="compact"
                          />
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] leading-tight">
                          <span
                            className={[
                              'rounded-full px-1.5 py-0.5 font-semibold uppercase',
                              r.deliverySpeed === 'EXPRESS'
                                ? 'bg-rose-100 text-rose-800'
                                : 'bg-slate-100 text-slate-700',
                            ].join(' ')}
                          >
                            {r.deliverySpeed}
                          </span>
                          <span
                            className={[
                              'rounded-full px-1.5 py-0.5 font-semibold uppercase',
                              r.status === 'DISPATCHED'
                                ? 'bg-amber-200 text-amber-950'
                                : r.status === 'OPEN'
                                  ? 'bg-slate-100 text-slate-800'
                                  : 'bg-slate-200 text-slate-700',
                            ].join(' ')}
                          >
                            {presentBatchStatus(r.status)}
                          </span>
                          <span className="text-slate-600">
                            {t('backoffice.delivery_batches_orders_count', { count: r.orderCount })}
                          </span>
                          {r.assignedShippingAgent ? (
                            <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">
                              {r.assignedShippingAgent.fullName ||
                                r.assignedShippingAgent.email}
                            </span>
                          ) : null}
                          {r.status === 'DISPATCHED' &&
                          (r.dispatchJobStatus === 'QUEUED' || r.dispatchJobStatus === 'RUNNING') &&
                          typeof r.dispatchJobTotal === 'number' ? (
                            <span className="w-full truncate text-amber-800">
                              {t('backoffice.delivery_batches_dispatch_progress', {
                                processed: r.dispatchJobProcessed ?? 0,
                                total: r.dispatchJobTotal,
                              })}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            {!selectedId ? (
              <p className="text-sm text-slate-600">{t('backoffice.delivery_batches_select')}</p>
            ) : detailLoading ? (
              <p className="text-sm text-slate-600">{t('common.loading')}</p>
            ) : !detail ? (
              <p className="text-sm text-slate-600">{t('backoffice.delivery_batches_detail_error')}</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    {detail.name || detail.areaLabel}
                  </h3>
                  <p className="text-xs text-slate-600">{detail.areaLabel}</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                    STATUS: {presentBatchStatus(detail.status)}
                  </p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                    {t('backoffice.delivery_batches_speed', { defaultValue: 'Delivery speed' })}: {detail.deliverySpeed}
                  </p>
                  {(detail.country || detail.cityKey) && (
                    <p className="mt-1 text-xs text-slate-500">
                      {[detail.cityKey, detail.country].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  <div className="mt-2">
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('backoffice.delivery_batches_estimated_window')}
                    </p>
                    <DeliveryWindowBadge
                      windowStart={detail.windowStart}
                      windowEnd={detail.windowEnd}
                      fromLabel={t('account.delivery_window_from')}
                      untilLabel={t('account.delivery_window_until')}
                      variant="split"
                    />
                  </div>
                  {detail.notes ? (
                    <p className="mt-2 text-xs text-slate-600">{detail.notes}</p>
                  ) : null}
                </div>
                {canUpdate ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-800">Batch settings</p>
                      <button
                        type="button"
                        onClick={() => setEditOpen((v) => !v)}
                        className="text-xs font-semibold text-slate-700 underline"
                      >
                        {editOpen ? 'Cancel edit' : 'Edit batch'}
                      </button>
                    </div>
                    {editOpen ? (
                      <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        <label className="text-xs font-medium text-slate-600">
                          Area label
                          <input
                            value={editAreaLabel}
                            onChange={(e) => setEditAreaLabel(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          />
                        </label>
                        <label className="text-xs font-medium text-slate-600">
                          Name (optional)
                          <input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          />
                        </label>
                        <label className="text-xs font-medium text-slate-600">
                          Delivery speed
                          <select
                            value={editDeliverySpeed}
                            onChange={(e) => setEditDeliverySpeed((e.target.value as 'STANDARD' | 'EXPRESS') || 'STANDARD')}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          >
                            <option value="STANDARD">STANDARD</option>
                            <option value="EXPRESS">EXPRESS</option>
                          </select>
                        </label>
                        <label className="text-xs font-medium text-slate-600">
                          Shipping agent
                          <select
                            value={editAssignedAgentId}
                            onChange={(e) => setEditAssignedAgentId(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          >
                            <option value="">— Unassigned —</option>
                            {shippingAgents.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.fullName ? `${a.fullName} (${a.email})` : a.email}
                              </option>
                            ))}
                          </select>
                          <span className="mt-1 block text-[11px] font-normal text-slate-500">
                            The agent will only see this batch when scanning
                            delivery codes.
                          </span>
                        </label>
                        <label className="text-xs font-medium text-slate-600">
                          Country
                          <input
                            value={editCountry}
                            onChange={(e) => setEditCountry(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          />
                        </label>
                        <label className="text-xs font-medium text-slate-600">
                          City / town
                          <input
                            value={editCity}
                            onChange={(e) => setEditCity(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          />
                        </label>
                        <label className="text-xs font-medium text-slate-600">
                          Window start
                          <input
                            type="datetime-local"
                            value={editWindowStart}
                            onChange={(e) => setEditWindowStart(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          />
                        </label>
                        <label className="text-xs font-medium text-slate-600">
                          Window end
                          <input
                            type="datetime-local"
                            value={editWindowEnd}
                            onChange={(e) => setEditWindowEnd(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          />
                        </label>
                        <label className="text-xs font-medium text-slate-600 sm:col-span-2 lg:col-span-3">
                          Notes
                          <textarea
                            rows={2}
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          />
                        </label>
                        <div className="sm:col-span-2 lg:col-span-3">
                          <button
                            type="button"
                            disabled={saving || !editAreaLabel.trim()}
                            onClick={() => void handleUpdateBatch()}
                            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                          >
                            {saving ? t('common.loading') : 'Save batch changes'}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {canUpdate && !USE_CARRIER_FULFILLMENT ? (
                  <div className="space-y-2">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                        Batch workflow timeline
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] font-semibold">
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800">OPEN</span>
                        <span className="text-slate-400">→</span>
                        <span
                          className={[
                            'rounded-full px-2 py-0.5',
                            detail.status === 'OPEN'
                              ? 'bg-slate-100 text-slate-600'
                              : 'bg-emerald-100 text-emerald-800',
                          ].join(' ')}
                        >
                          SHIPPED
                        </span>
                        <span className="text-slate-400">→</span>
                        <span
                          className={[
                            'rounded-full px-2 py-0.5',
                            detail.status === 'CLOSED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : detail.status === 'SHIPPED'
                                ? 'bg-indigo-100 text-indigo-800'
                                : 'bg-slate-100 text-slate-600',
                          ].join(' ')}
                        >
                          DELIVERY CONFIRMED
                        </span>
                        <span className="text-slate-400">→</span>
                        <span
                          className={[
                            'rounded-full px-2 py-0.5',
                            detail.status === 'CLOSED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-slate-100 text-slate-600',
                          ].join(' ')}
                        >
                          CLOSED
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={saving || detail.status !== 'OPEN' || detail.orders.length === 0}
                        onClick={() => openConfirmModal('SHIP_NOW', detail.id)}
                        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-45"
                      >
                        {t('backoffice.delivery_batches_ship_now', { defaultValue: 'SHIP NOW' })}
                      </button>
                      <button
                        type="button"
                        disabled={saving || detail.status !== 'SHIPPED'}
                        onClick={() => openConfirmModal('CONFIRM_DELIVERY', detail.id)}
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-45"
                      >
                        {t('backoffice.delivery_batches_confirm_delivery', { defaultValue: 'CONFIRM DELIVERY' })}
                      </button>
                      <button
                        type="button"
                        disabled={saving || detail.status !== 'SHIPPED'}
                        onClick={() => openConfirmModal('CLOSE_BATCH', detail.id)}
                        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-45"
                      >
                        {t('backoffice.delivery_batches_close_batch', { defaultValue: 'CLOSE BATCH' })}
                      </button>
                    </div>
                    {detail.status === 'OPEN' && detail.orders.length === 0 ? (
                      <p className="text-[11px] font-medium text-amber-700">
                        Add at least one order to this batch before shipping.
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {!USE_CARRIER_FULFILLMENT && detail.status === 'DISPATCHED' ? (
                  <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
                    {detail.dispatchJobStatus === 'FAILED' ? (
                      <p className="font-semibold text-rose-800">
                        {t('backoffice.delivery_batches_dispatch_failed')}
                      </p>
                    ) : (
                      <>
                        <p className="font-medium text-amber-950">
                          {t('backoffice.delivery_batches_dispatching')}
                        </p>
                        {typeof detail.dispatchJobTotal === 'number' ? (
                          <>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-amber-200/80">
                              <div
                                className="h-2 rounded-full bg-amber-500 transition-all duration-300"
                                style={{
                                  width: `${detail.dispatchJobTotal === 0 ? 100 : Math.min(100, ((detail.dispatchJobProcessed ?? 0) / detail.dispatchJobTotal) * 100)}%`,
                                }}
                              />
                            </div>
                            <p className="font-medium text-amber-950">
                              {t('backoffice.delivery_batches_dispatch_progress', {
                                processed: detail.dispatchJobProcessed ?? 0,
                                total: detail.dispatchJobTotal,
                              })}
                            </p>
                          </>
                        ) : null}
                        <p className="text-amber-900/90">
                          {t('backoffice.delivery_batches_dispatch_background')}
                        </p>
                      </>
                    )}
                    {Array.isArray(detail.dispatchJobErrors) && detail.dispatchJobErrors.length > 0 ? (
                      <ul className="mt-1 max-h-36 list-inside list-disc overflow-y-auto text-[11px] text-rose-900">
                        {detail.dispatchJobErrors.map((e) => (
                          <li key={`${e.orderId}-${e.message.slice(0, 48)}`}>
                            <span className="font-mono">{formatOrderReference(e.orderId)}</span>: {e.message}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ) : null}
                {detail.status === 'CLOSED' &&
                Array.isArray(detail.dispatchJobErrors) &&
                detail.dispatchJobErrors.length > 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-xs text-amber-950">
                    <p className="font-semibold">{t('backoffice.delivery_batches_dispatch_partial_title')}</p>
                    <ul className="mt-1 max-h-36 list-inside list-disc overflow-y-auto text-[11px] text-amber-900">
                      {detail.dispatchJobErrors.map((e) => (
                        <li key={`${e.orderId}-${e.message.slice(0, 48)}`}>
                          <span className="font-mono">{formatOrderReference(e.orderId)}</span>: {e.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {detail.status === 'CLOSED' && detail.dispatchStats ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-900">
                    <p className="font-semibold">Dispatch summary</p>
                    <div className="mt-1 grid gap-1 sm:grid-cols-2">
                      <p>
                        Attempted: <span className="font-semibold">{detail.dispatchStats.attempted}</span>
                      </p>
                      <p>
                        Shipped: <span className="font-semibold text-emerald-700">{detail.dispatchStats.shipped}</span>
                      </p>
                      <p>
                        Left: <span className="font-semibold text-amber-700">{detail.dispatchStats.left}</span>
                      </p>
                      <p>
                        Failed: <span className="font-semibold text-rose-700">{detail.dispatchStats.failed}</span>
                      </p>
                    </div>
                    {detail.dispatchStats.reasonBreakdown.length > 0 ? (
                      <div className="mt-2">
                        <p className="font-medium text-slate-800">Left/failed reasons</p>
                        <ul className="mt-1 list-inside list-disc space-y-0.5 text-[11px] text-slate-700">
                          {detail.dispatchStats.reasonBreakdown.map((r) => (
                            <li key={`${r.reason}-${r.count}`}>
                              {r.reason} ({r.count})
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {canDelete ? (
                  <button
                    type="button"
                    disabled={saving || !detail || detail.orders.length > 0 || detail.status !== 'OPEN'}
                    onClick={() => openConfirmModal('DELETE', detail.id)}
                    className="text-xs font-semibold text-rose-700 underline disabled:opacity-50"
                  >
                    {t('backoffice.delivery_batches_delete')}
                  </button>
                ) : null}

                {canUpdate && detail.status === 'OPEN' ? (
                  <div className="rounded-lg border border-slate-100 bg-slate-50/80 p-3">
                    <p className="text-xs font-semibold text-slate-800">
                      {t('backoffice.delivery_batches_add_order')}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {t('backoffice.delivery_batches_search_hint')}
                    </p>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <label className="block text-xs font-medium text-slate-600">
                        {t('backoffice.delivery_batches_assign_country')}
                        <input
                          className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          value={assignSearchCountry}
                          onChange={(e) => setAssignSearchCountry(e.target.value)}
                          placeholder={t('backoffice.delivery_batches_assign_country_ph')}
                          autoComplete="off"
                          list={`dl-batch-country-${detail.id}`}
                        />
                        <datalist id={`dl-batch-country-${detail.id}`}>
                          {countrySuggestions.map((s) => (
                            <option key={s} value={s} />
                          ))}
                        </datalist>
                      </label>
                      <label className="block text-xs font-medium text-slate-600">
                        {t('backoffice.delivery_batches_assign_town')}
                        <input
                          className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          value={assignSearchTown}
                          onChange={(e) => setAssignSearchTown(e.target.value)}
                          placeholder={t('backoffice.delivery_batches_assign_town_ph')}
                          autoComplete="off"
                          list={`dl-batch-city-${detail.id}`}
                        />
                        <datalist id={`dl-batch-city-${detail.id}`}>
                          {citySuggestions.map((s) => (
                            <option key={s} value={s} />
                          ))}
                        </datalist>
                      </label>
                    </div>
                    <label className="mt-2 block text-xs font-medium text-slate-600">
                      {t('backoffice.delivery_batches_order_search')}
                      <input
                        type="search"
                        value={assignSearchQuery}
                        onChange={(e) => setAssignSearchQuery(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                        placeholder={t('backoffice.delivery_batches_order_search_ph')}
                        autoComplete="off"
                      />
                    </label>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {searchLoading && hasSearchInput ? (
                        <span className="text-[11px] font-medium text-slate-500">
                          {t('backoffice.delivery_batches_searching')}
                        </span>
                      ) : null}
                      {hasSearchInput && !searchLoading && assignable.length > 0 ? (
                        <>
                          <button
                            type="button"
                            onClick={selectAllShown}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                          >
                            {t('backoffice.delivery_batches_select_all_results')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedOrderIds([])}
                            className="rounded-lg border border-transparent px-3 py-1.5 text-xs font-medium text-slate-600 underline"
                          >
                            {t('backoffice.delivery_batches_clear_selection')}
                          </button>
                        </>
                      ) : null}
                    </div>
                    {selectedOrderIds.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {selectedOrderIds.map((id) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => toggleOrderSelected(id)}
                            className="inline-flex rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 font-mono text-[11px] font-medium text-brand-900"
                          >
                            {id.slice(0, 8)}… ×
                          </button>
                        ))}
                      </div>
                    ) : null}
                    <p className="mt-2 text-[11px] text-slate-600">
                      {t('backoffice.delivery_batches_selected_n', { count: selectedOrderIds.length })}
                    </p>
                    <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                      {!hasSearchInput ? (
                        <p className="p-3 text-xs text-slate-500">
                          {t('backoffice.delivery_batches_search_empty_prompt')}
                        </p>
                      ) : searchLoading ? (
                        <p className="p-3 text-xs text-slate-500">{t('common.loading')}</p>
                      ) : assignable.length === 0 ? (
                        <p className="p-3 text-xs text-slate-500">
                          {t('backoffice.delivery_batches_no_search_matches')}
                        </p>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {groupedByArea.map(([areaKey, ordersInArea]) => {
                            const [countryLabel, cityLabel] = areaKey.split(AREA_GROUP_SEP);
                            return (
                              <div key={areaKey}>
                                <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50/90 px-2 py-1.5">
                                  <span className="text-[11px] font-semibold text-slate-700">
                                    {cityLabel === '—' && countryLabel === '—'
                                      ? t('backoffice.delivery_batches_area_unknown')
                                      : [cityLabel, countryLabel].filter((x) => x && x !== '—').join(', ')}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => selectAllInArea(ordersInArea.map((o) => o.id))}
                                    className="text-[11px] font-semibold text-slate-800 underline"
                                  >
                                    {t('backoffice.delivery_batches_select_all_area')}
                                  </button>
                                </div>
                                <ul>
                                  {ordersInArea.map((o) => {
                                    const sel = selectedOrderIds.includes(o.id);
                                    const other = o.deliveryBatch;
                                    const snap = o.addressSnapshot as {
                                      city?: string;
                                      country?: string;
                                    };
                                    return (
                                      <li key={o.id}>
                                        <button
                                          type="button"
                                          onClick={() => toggleOrderSelected(o.id)}
                                          className={[
                                            'flex w-full flex-col gap-0.5 px-3 py-2 text-left text-xs transition',
                                            sel ? 'bg-brand-50' : 'hover:bg-slate-50',
                                          ].join(' ')}
                                        >
                                          <span className="font-mono text-[11px] text-slate-900">
                                            {formatOrderReference(o.id)}
                                          </span>
                                          <span className="text-slate-600">
                                            {snap?.city}, {snap?.country}
                                            {o.customer?.customerProfile?.fullName
                                              ? ` · ${o.customer.customerProfile.fullName}`
                                              : ''}
                                          </span>
                                          {other && other.id !== selectedId ? (
                                            <span className="text-[10px] text-amber-800">
                                              {t('backoffice.delivery_batches_other_batch', {
                                                batch: other.name || other.areaLabel,
                                              })}
                                            </span>
                                          ) : null}
                                        </button>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={assignBusy || selectedOrderIds.length === 0}
                      onClick={() => void assignSelectedOrders()}
                      className="mt-3 w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50 sm:w-auto"
                    >
                      {assignBusy
                        ? t('common.loading')
                        : t('backoffice.delivery_batches_assign_selected')}
                    </button>
                  </div>
                ) : null}

                {USE_CARRIER_FULFILLMENT && detail ? (
                  <CarrierBatchShipPanel
                    batchId={detail.id}
                    batchStatus={detail.status}
                    orders={detail.orders}
                  />
                ) : null}

                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {USE_CARRIER_FULFILLMENT
                      ? t('backoffice.carrier_batch_orders_title', { defaultValue: 'Orders in batch' })
                      : t('backoffice.delivery_batches_in_batch')}
                  </h4>
                  {detail.orders.length === 0 ? (
                    <p className="mt-2 text-sm text-slate-600">
                      {t('backoffice.delivery_batches_no_orders')}
                    </p>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {detail.orders.map((o) => (
                        <li
                          key={o.id}
                          className="rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <span className="font-mono text-xs text-slate-800">{formatOrderReference(o.id)}</span>
                              <div className="text-xs text-slate-600">
                                {o.customer?.customerProfile?.fullName || o.customer?.email}
                              </div>
                              <div className="text-[11px] text-slate-500">
                                {(o.addressSnapshot as { city?: string })?.city},{' '}
                                {(o.addressSnapshot as { country?: string })?.country}
                              </div>
                              <div className="text-[11px] text-slate-500">
                                {!USE_CARRIER_FULFILLMENT ? (
                                  <>
                                    {t('backoffice.delivery_batches_speed')}: {o.deliverySpeed}
                                  </>
                                ) : (
                                  <>
                                    {o.carrierServiceName || o.carrierServiceCode || 'FedEx'}
                                  </>
                                )}
                              </div>
                            </div>
                            {canUpdate && detail.status === 'OPEN' ? (
                              <button
                                type="button"
                                disabled={assignBusy}
                                onClick={() => void unassignOrder(o.id)}
                                className="text-[11px] font-semibold text-rose-700 underline"
                              >
                                {t('backoffice.delivery_batches_remove')}
                              </button>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {confirmModal.open ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 px-4">
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900">
              {confirmModal.kind === 'SHIP_NOW'
                ? t('backoffice.delivery_batches_ship_now', { defaultValue: 'SHIP NOW' })
                : confirmModal.kind === 'CONFIRM_DELIVERY'
                  ? t('backoffice.delivery_batches_confirm_delivery', { defaultValue: 'CONFIRM DELIVERY' })
                  : confirmModal.kind === 'CLOSE_BATCH'
                    ? t('backoffice.delivery_batches_close_batch', { defaultValue: 'CLOSE BATCH' })
                : t('backoffice.delivery_batches_delete')}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {confirmModal.kind === 'SHIP_NOW'
                ? t('backoffice.delivery_batches_ship_now_confirm', {
                    defaultValue:
                      'Ship all eligible orders in this batch now?',
                  })
                : confirmModal.kind === 'CONFIRM_DELIVERY'
                  ? 'Mark shipped orders in this batch as delivered.'
                  : confirmModal.kind === 'CLOSE_BATCH'
                    ? 'Close this batch after delivery confirmation is complete.'
                : t('backoffice.delivery_batches_delete_confirm')}
            </p>
            {confirmModal.kind === 'CONFIRM_DELIVERY' ? (
              <div className="mt-3 space-y-2">
                <textarea
                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  placeholder="Note (optional)"
                  value={confirmModal.note || ''}
                  onChange={(e) => setConfirmModal((prev) => ({ ...(prev as any), note: e.target.value }))}
                />
                <div
                  className={[
                    'rounded-lg border px-3 py-2.5',
                    confirmModal.hasIssueOrders
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-slate-200 bg-slate-50',
                  ].join(' ')}
                >
                  <label className="flex cursor-pointer items-start gap-2.5">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4"
                      checked={Boolean(confirmModal.hasIssueOrders)}
                      onChange={(e) =>
                        setConfirmModal((prev) => ({
                          ...(prev as any),
                          hasIssueOrders: e.target.checked,
                          issueOrderIds: e.target.checked ? prev?.issueOrderIds || [] : [],
                          issueSearch: e.target.checked ? prev?.issueSearch || '' : '',
                        }))
                      }
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-amber-900">
                        Some orders have issues and should be handled individually
                      </span>
                      <span className="mt-0.5 block text-[11px] text-amber-800">
                        Select this to remove issue orders from the batch before delivery confirmation.
                      </span>
                    </span>
                  </label>
                </div>
                {confirmModal.hasIssueOrders ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50/70 p-2">
                    <p className="text-[11px] text-amber-900">
                      Select issue orders to remove from this batch now. They will not be marked delivered.
                    </p>
                    <input
                      type="search"
                      className="mt-2 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                      placeholder="Search order id / customer"
                      value={confirmModal.issueSearch || ''}
                      onChange={(e) =>
                        setConfirmModal((prev) => ({
                          ...(prev as any),
                          issueSearch: e.target.value,
                        }))
                      }
                    />
                    <div className="mt-2 max-h-40 overflow-y-auto rounded border border-slate-200 bg-white">
                      {(detail?.orders || [])
                        .filter((o) => {
                          const q = String(confirmModal.issueSearch || '').trim().toLowerCase();
                          if (!q) return true;
                          const hay = [
                            o.id,
                            o.customer?.email,
                            o.customer?.customerProfile?.fullName,
                          ]
                            .filter(Boolean)
                            .join(' ')
                            .toLowerCase();
                          return hay.includes(q);
                        })
                        .map((o) => {
                          const sel = (confirmModal.issueOrderIds || []).includes(o.id);
                          return (
                            <label
                              key={`issue-${o.id}`}
                              className="flex cursor-pointer items-start gap-2 border-b border-slate-100 px-2 py-1.5 last:border-b-0"
                            >
                              <input
                                type="checkbox"
                                checked={sel}
                                onChange={(e) =>
                                  setConfirmModal((prev) => {
                                    const current = new Set(prev?.issueOrderIds || []);
                                    if (e.target.checked) current.add(o.id);
                                    else current.delete(o.id);
                                    return { ...(prev as any), issueOrderIds: Array.from(current) };
                                  })
                                }
                              />
                              <span className="min-w-0 text-[11px] text-slate-700">
                                <span className="font-mono text-slate-900">{formatOrderReference(o.id)}</span>
                                {o.customer?.customerProfile?.fullName
                                  ? ` · ${o.customer.customerProfile.fullName}`
                                  : o.customer?.email
                                    ? ` · ${o.customer.email}`
                                    : ''}
                              </span>
                            </label>
                          );
                        })}
                    </div>
                    <p className="mt-1 text-[11px] text-amber-900">
                      Selected issue orders: {(confirmModal.issueOrderIds || []).length}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeConfirmModal}
                disabled={saving}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void runConfirmedAction()}
                disabled={saving}
                className={[
                  'rounded-lg px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60',
                  confirmModal.kind === 'DELETE'
                    ? 'bg-rose-600 hover:bg-rose-500'
                    : 'bg-slate-900 hover:bg-slate-800',
                ].join(' ')}
              >
                {confirmModal.kind === 'SHIP_NOW'
                  ? t('backoffice.delivery_batches_ship_now', { defaultValue: 'SHIP NOW' })
                  : confirmModal.kind === 'CONFIRM_DELIVERY'
                    ? t('backoffice.delivery_batches_confirm_delivery', { defaultValue: 'CONFIRM DELIVERY' })
                    : confirmModal.kind === 'CLOSE_BATCH'
                      ? t('backoffice.delivery_batches_close_batch', { defaultValue: 'CLOSE BATCH' })
                    : t('common.ok', { defaultValue: 'OK' })}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </main>
    </MainLayout>
  );
}
